// components/RoundedImage.tsx (Enhanced Version)
import React, { useState } from 'react';

interface RoundedImageProps {
  /** Image URL to display */
  imageUrl?: string;
  /** Alt text for accessibility */
  alt?: string;
  /** Additional CSS classes */
  className?: string;
  /** Size of the image */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Fallback text when image fails to load */
  fallbackText?: string;
  /** Fallback icon (emoji or React node) */
  fallbackIcon?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Whether to show a loading skeleton */
  showLoading?: boolean;
  /** Border radius style */
  rounded?: 'full' | 'lg' | 'xl' | '2xl' | 'none';
  /** Gradient colors for fallback */
  gradient?: string;
}

export function RoundedImage({
  imageUrl,
  alt = 'Image',
  className = '',
  size = 'md',
  fallbackText = '',
  fallbackIcon,
  onClick,
  showLoading = false,
  rounded = 'full',
  gradient = 'from-blue-400 to-cyan-400',
}: RoundedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-16 h-16 text-base',
    xl: 'w-24 h-24 text-lg',
    '2xl': 'w-32 h-32 text-xl',
  };

  const roundedClasses = {
    full: 'rounded-full',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    none: 'rounded-none',
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const getFallbackContent = () => {
    if (fallbackIcon) return fallbackIcon;
    if (fallbackText) return fallbackText;
    if (alt && alt !== 'Image' && alt.length > 0) {
      // Get initials from alt text
      const words = alt.split(' ');
      if (words.length > 1) {
        return words.map(w => w.charAt(0).toUpperCase()).slice(0, 2).join('');
      }
      return alt.charAt(0).toUpperCase();
    }
    return '📰';
  };

  // Loading skeleton
  if (showLoading && isLoading && !imageError) {
    return (
      <div
        className={`
          ${sizeClasses[size]}
          ${roundedClasses[rounded]}
          bg-gray-200 dark:bg-gray-700
          animate-pulse
          ${className}
        `}
      />
    );
  }

  // Fallback state
  if (!imageUrl || imageError) {
    return (
      <div
        className={`
          ${sizeClasses[size]}
          ${roundedClasses[rounded]}
          bg-gradient-to-br ${gradient}
          flex items-center justify-center 
          text-white font-bold
          ${className}
          ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
        `}
        onClick={onClick}
        role="img"
        aria-label={alt}
      >
        <span className="text-inherit">{getFallbackContent()}</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={`
        ${sizeClasses[size]}
        ${roundedClasses[rounded]}
        object-cover 
        ${className}
        ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
        ${isLoading ? 'opacity-0' : 'opacity-100'}
        transition-opacity duration-300
      `}
      onError={handleImageError}
      onLoad={handleImageLoad}
      onClick={onClick}
      loading="lazy"
      decoding="async"
    />
  );
}

// Component with built-in badge
interface RoundedImageWithBadgeProps extends RoundedImageProps {
  badgeCount?: number;
  badgeColor?: string;
  badgePosition?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
}

export function RoundedImageWithBadge({
  badgeCount,
  badgeColor = 'bg-red-500',
  badgePosition = 'top-right',
  ...props
}: RoundedImageWithBadgeProps) {
  const positionClasses = {
    'top-right': '-top-1 -right-1',
    'bottom-right': '-bottom-1 -right-1',
    'top-left': '-top-1 -left-1',
    'bottom-left': '-bottom-1 -left-1',
  };

  if (!badgeCount || badgeCount <= 0) {
    return <RoundedImage {...props} />;
  }

  return (
    <div className="relative inline-block">
      <RoundedImage {...props} />
      <span
        className={`
          absolute ${positionClasses[badgePosition]}
          w-5 h-5 ${badgeColor} 
          text-white text-[10px] font-bold 
          rounded-full flex items-center justify-center
          border-2 border-white dark:border-gray-800
          shadow-md
        `}
      >
        {badgeCount > 99 ? '99+' : badgeCount}
      </span>
    </div>
  );
}

// Component with status indicator
interface RoundedImageWithStatusProps extends RoundedImageProps {
  isOnline?: boolean;
  statusColor?: string;
  statusSize?: 'sm' | 'md' | 'lg';
}

export function RoundedImageWithStatus({
  isOnline = false,
  statusColor = 'bg-green-500',
  statusSize = 'md',
  ...props
}: RoundedImageWithStatusProps) {
  const statusSizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  return (
    <div className="relative inline-block">
      <RoundedImage {...props} />
      {isOnline && (
        <span
          className={`
            absolute bottom-0 right-0
            ${statusSizeClasses[statusSize]}
            ${statusColor}
            rounded-full
            border-2 border-white dark:border-gray-800
            shadow-sm
            animate-pulse
          `}
        />
      )}
    </div>
  );
}

export default RoundedImage;